/**
 * Metric Helper Utilities
 * Functions to work with the centralized METRICS_REGISTRY
 */

import { METRICS_REGISTRY, VARIANT_TYPES, METRIC_GROUPS, METRIC_FORMATS, METRIC_TYPES } from '../constants/metrics';

// ============================================
// LOOKUP FUNCTIONS
// ============================================

/**
 * Find a metric variant by field name
 * @param {string} fieldName - Display field name (e.g., "Revenue YOY%", "RPO")
 * @returns {Object|null} Variant object with all properties, or null if not found
 */
export const findMetricByField = (fieldName) => {
  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      if (variant.field === fieldName) {
        return {
          ...variant,
          metricId: metric.id,
          baseMetric: metric,
        };
      }
    }
  }
  return null;
};

/**
 * Find a metric variant by dbField name
 * @param {string} dbFieldName - Database field name (e.g., "revenueyoy", "ruleOf40values")
 * @returns {Object|null} Variant object, or null if not found
 */
export const findMetricByDbField = (dbFieldName) => {
  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      if (variant.dbField === dbFieldName) {
        return {
          ...variant,
          metricId: metric.id,
          baseMetric: metric,
        };
      }
    }
  }
  return null;
};

/**
 * Get all variants of a metric by metric ID
 * @param {string} metricId - Metric ID (e.g., "revenue", "rpo")
 * @returns {Object} Object with variant types as keys
 */
export const getMetricVariants = (metricId) => {
  const metric = METRICS_REGISTRY[metricId];
  if (!metric) return {};
  return metric.variants;
};

/**
 * Find related metric variant (e.g., growth variant for absolute metric)
 * @param {string} fieldName - Current field name
 * @param {string} targetVariantType - Desired variant type (from VARIANT_TYPES)
 * @returns {Object|null} Related variant, or null if not found
 */
export const findRelatedVariant = (fieldName, targetVariantType) => {
  const currentMetric = findMetricByField(fieldName);
  if (!currentMetric) return null;

  const variants = getMetricVariants(currentMetric.metricId);
  return variants[targetVariantType] || null;
};

/**
 * Get the absolute variant for any metric
 * @param {string} fieldName - Any variant field name
 * @returns {Object|null} Absolute variant if exists
 */
export const getAbsoluteVariant = (fieldName) => {
  return findRelatedVariant(fieldName, VARIANT_TYPES.ABSOLUTE);
};

/**
 * Get the growth variant for any metric
 * @param {string} fieldName - Any variant field name
 * @returns {Object|null} Growth variant if exists
 */
export const getGrowthVariant = (fieldName) => {
  return findRelatedVariant(fieldName, VARIANT_TYPES.GROWTH);
};

// ============================================
// FILTERING FUNCTIONS
// ============================================

/**
 * Get all metrics for a specific group
 * @param {string} groupName - Group name from METRIC_GROUPS
 * @param {string} variantType - Optional: filter by variant type
 * @returns {Array} Array of metric variants
 */
export const getMetricsByGroup = (groupName, variantType = null) => {
  const metrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    if (metric.group !== groupName) continue;

    for (const [type, variant] of Object.entries(metric.variants)) {
      if (variantType === null || type === variantType) {
        metrics.push({
          ...variant,
          metricId: metric.id,
          variantType: type,
        });
      }
    }
  }

  return metrics;
};

/**
 * Get all metrics by format type
 * @param {string} format - Format from METRIC_FORMATS
 * @returns {Array} Array of metric variants
 */
export const getMetricsByFormat = (format) => {
  const metrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      if (variant.format === format) {
        metrics.push({
          ...variant,
          metricId: metric.id,
        });
      }
    }
  }

  return metrics;
};

/**
 * Get all PRO metrics
 * @returns {Array} Array of PRO metric variants
 */
export const getProMetrics = () => {
  const metrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      if (variant.proMetric) {
        metrics.push({
          ...variant,
          metricId: metric.id,
        });
      }
    }
  }

  return metrics;
};

/**
 * Get all free (non-PRO) metrics
 * @returns {Array} Array of free metric variants
 */
export const getFreeMetrics = () => {
  const metrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      if (!variant.proMetric) {
        metrics.push({
          ...variant,
          metricId: metric.id,
        });
      }
    }
  }

  return metrics;
};

// ============================================
// COLUMN DEFINITION BUILDERS
// ============================================

/**
 * Build AG Grid column definition from metric variant
 * @param {Object} metricVariant - Metric variant object
 * @param {Object} customProps - Additional AG Grid column properties
 * @returns {Object} AG Grid column definition
 */
export const buildColumnDef = (metricVariant, customProps = {}) => {
  return {
    field: metricVariant.field,
    headerName: metricVariant.headerName || metricVariant.field,
    group: metricVariant.baseMetric?.group,
    format: metricVariant.format,
    proMetric: metricVariant.proMetric,
    ...customProps,
  };
};

/**
 * Build column definitions for a specific group
 * @param {string} groupName - Group name
 * @param {Object} customProps - Additional properties
 * @returns {Array} Array of column definitions
 */
export const buildColumnDefsForGroup = (groupName, customProps = {}) => {
  const metrics = getMetricsByGroup(groupName);
  return metrics.map(metric => buildColumnDef(metric, customProps));
};

// ============================================
// NAVIGATION MAPPING FUNCTIONS
// ============================================

/**
 * Map a metric for cross-view navigation
 * When navigating from SecurityAnalysis (showing absolute "RPO") to Dashboard (needs "RPO YOY%")
 * @param {string} fieldName - Current field name
 * @param {string} targetView - Target view ('dashboard', 'securityAnalysis', 'valuation')
 * @returns {string} Mapped field name for target view
 */
export const mapMetricForNavigation = (fieldName, targetView) => {
  const currentMetric = findMetricByField(fieldName);
  if (!currentMetric) return fieldName;

  const variants = getMetricVariants(currentMetric.metricId);

  // Navigation rules:
  // - Dashboard/Valuation prefer growth variants (YOY%)
  // - SecurityAnalysis prefers absolute variants
  switch (targetView) {
    case 'dashboard':
    case 'valuation':
      // Prefer growth variant, fall back to TTM growth, then any variant
      return variants[VARIANT_TYPES.GROWTH]?.field ||
             variants[VARIANT_TYPES.TTM_GROWTH]?.field ||
             variants[VARIANT_TYPES.RATIO]?.field ||
             fieldName;

    case 'securityAnalysis':
      // Prefer absolute variant
      return variants[VARIANT_TYPES.ABSOLUTE]?.field || fieldName;

    default:
      return fieldName;
  }
};

/**
 * Resolve a metric for a target view only when that view can display it.
 * Falls back instead of returning an unsupported mapped metric.
 *
 * @param {string} fieldName - Current/global field name
 * @param {string} targetView - Target view key from metric variant views
 * @param {Object} options
 * @param {Set|string[]} options.validFields - Optional fields available in the current component
 * @param {string} options.fallback - Fallback field when no supported mapping exists
 * @returns {string}
 */
export const resolveMetricForView = (fieldName, targetView, { validFields, fallback } = {}) => {
  const validFieldSet = validFields
    ? validFields instanceof Set ? validFields : new Set(validFields)
    : null;

  const isUsable = (variant) => {
    if (!variant?.field) return false;
    if (!variant.views?.[targetView]) return false;
    return !validFieldSet || validFieldSet.has(variant.field);
  };

  const currentMetric = findMetricByField(fieldName);
  if (!currentMetric) {
    return validFieldSet?.has(fieldName) ? fieldName : fallback;
  }

  const variants = getMetricVariants(currentMetric.metricId);

  const preferredVariantTypes = (() => {
    switch (targetView) {
      case 'dashboard':
      case 'valuation':
        return [
          VARIANT_TYPES.GROWTH,
          VARIANT_TYPES.TTM_GROWTH,
          VARIANT_TYPES.RATIO,
          VARIANT_TYPES.MULTIPLE,
          VARIANT_TYPES.ACCELERATION,
        ];
      case 'securityAnalysis':
        return [
          VARIANT_TYPES.ABSOLUTE,
          VARIANT_TYPES.RATIO,
          VARIANT_TYPES.PERCENT_OF,
          VARIANT_TYPES.GROWTH,
        ];
      default:
        return Object.keys(variants);
    }
  })();

  for (const variantType of preferredVariantTypes) {
    if (isUsable(variants[variantType])) {
      return variants[variantType].field;
    }
  }

  const currentVariant = Object.values(variants).find(variant => variant.field === fieldName);
  if (isUsable(currentVariant)) {
    return currentVariant.field;
  }

  const firstSupported = Object.values(variants).find(isUsable);
  return firstSupported?.field || fallback;
};

/**
 * Get storage key for a metric (what gets saved to localStorage)
 * Defaults to growth variant for consistency
 * @param {string} fieldName - Display field name
 * @returns {string} Field name to store
 */
export const getMetricStorageKey = (fieldName) => {
  const currentMetric = findMetricByField(fieldName);
  if (!currentMetric) return fieldName;

  const variants = getMetricVariants(currentMetric.metricId);

  // Store the growth variant if available (most common representation)
  return variants[VARIANT_TYPES.GROWTH]?.field ||
         variants[VARIANT_TYPES.TTM_GROWTH]?.field ||
         fieldName;
};

/**
 * Load metric from storage and map to appropriate variant for current view
 * @param {string} storedFieldName - Field name from localStorage
 * @param {string} currentView - Current view context
 * @returns {string} Field name appropriate for current view
 */
export const loadMetricFromStorage = (storedFieldName, currentView) => {
  return mapMetricForNavigation(storedFieldName, currentView);
};

// ============================================
// VIEW-BASED METRIC GETTERS
// ============================================

/**
 * Universal getter — returns all metric variants that declare a given view.
 * Replaces all algorithmic filtering functions.
 * @param {string} viewName - View key (compSheet, dashboard, valuation, securityAnalysis, trended, trajectory, margins)
 * @param {boolean} isPaid - Whether user has paid subscription (default true)
 * @returns {Array} Array of metric variants for the view
 */
export const getMetricsForView = (viewName, isPaid = true) => {
  const metrics = [];
  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const [type, variant] of Object.entries(metric.variants)) {
      if (!variant.views?.[viewName]) continue;
      if (!isPaid && variant.proMetric) continue;
      metrics.push({
        ...variant,
        metricId: metric.id,
        variantType: type,
        group: variant.group ?? metric.group,
        baseMetric: metric,
      });
    }
  }
  return metrics;
};

/**
 * Get display-only placements for a metric variant in a specific view.
 * Supports the canonical displayPlacements export and the view-scoped placements shape.
 * @param {Object} metricVariant - Metric variant from the registry or field mapping
 * @param {string} viewName - View key (dashboard, model, etc.)
 * @returns {Array} Normalized placement objects
 */
export const getDisplayPlacementsForView = (metricVariant, viewName) => {
  if (!metricVariant || !viewName) return [];

  const displayPlacements = (metricVariant.displayPlacements || [])
    .filter(placement => placement.view === viewName);

  const scopedPlacements = (metricVariant.views?.[viewName]?.placements || [])
    .map(placement => ({ ...placement, view: viewName }));

  const seen = new Set();
  return [...displayPlacements, ...scopedPlacements]
    .filter(placement => placement?.group)
    .filter(placement => {
      const key = [
        placement.view,
        placement.group,
        placement.section || '',
        placement.order ?? '',
      ].join('|');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
};

/**
 * Get metrics for Dashboard
 * @param {boolean} isPaid - Whether user has paid subscription
 * @returns {Array} Filtered array of metrics
 */
export const getDashboardMetrics = (isPaid) => getMetricsForView('dashboard', isPaid)
  .filter(metric => metric.views?.dashboard === true);

/**
 * Get metrics for Security Analysis
 * @returns {Array} Array of metrics for security analysis views
 */
export const getSecurityAnalysisMetrics = () => getMetricsForView('securityAnalysis');

/**
 * Get metrics for Valuation scatter plot
 * @returns {Object} Object with xMetrics and yMetrics arrays
 */
export const getValuationMetrics = () => {
  const xMetrics = [];
  const yMetrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const [type, variant] of Object.entries(metric.variants)) {
      const viewConfig = variant.views?.valuation;
      if (!viewConfig) continue;

      const enriched = {
        ...variant,
        metricId: metric.id,
        variantType: type,
        group: variant.group ?? metric.group,
        baseMetric: metric,
      };

      if (viewConfig.axis === 'x') xMetrics.push(enriched);
      else if (viewConfig.axis === 'y') yMetrics.push(enriched);
    }
  }

  return { xMetrics, yMetrics };
};

/**
 * Get metrics for Trended boxplot view
 * @returns {Array} Array of { field, group, format, proMetric } objects
 */
export const getTrendedMetrics = () => {
  const TRENDED_FREE_FIELDS = new Set(['Revenue YOY%']);

  return getMetricsForView('trended').map(m => ({
    field: m.field,
    group: m.group,
    format: m.format,
    proMetric: !TRENDED_FREE_FIELDS.has(m.field),
  }));
};

/**
 * Get metrics for Trajectory growth chart
 * @returns {Object} { baseMetrics, ttmMetrics } shaped for Trajectory.jsx
 */
export const getTrajectoryMetrics = () => {
  const baseMetrics = [];
  const ttmMetrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      const trajConfig = variant.views?.trajectory;
      if (!trajConfig) continue;

      baseMetrics.push({
        field: variant.field,
        label: trajConfig.label,
        color: trajConfig.color,
      });

      // If this metric has TTM config, add the TTM metric
      if (trajConfig.ttm) {
        ttmMetrics.push({
          field: `TTM ${trajConfig.ttm.sourceField || variant.field}`,
          label: trajConfig.ttm.label,
          color: trajConfig.ttm.color,
          calculated: true,
          sourceField: trajConfig.ttm.sourceField || variant.field,
        });
      }
    }
  }

  return { baseMetrics, ttmMetrics };
};

/**
 * Get metrics for Margins profitability chart
 * @returns {Object} { marginMetrics, opexMetrics } split by type
 */
export const getMarginMetrics = () => {
  const marginMetrics = [];
  const opexMetrics = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      const marginConfig = variant.views?.margins;
      if (!marginConfig) continue;

      const entry = {
        field: variant.field,
        label: marginConfig.label,
        color: marginConfig.color,
      };

      if (marginConfig.type === 'margin') marginMetrics.push(entry);
      else if (marginConfig.type === 'opex') opexMetrics.push(entry);
    }
  }

  return { marginMetrics, opexMetrics };
};

/**
 * Get numerical fields for CompSheet mean/median calculations
 * @returns {Array} Array of field names suitable for aggregation
 */
export const getCompSheetNumericalFields = () => {
  const fields = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const variant of Object.values(metric.variants)) {
      // Include all numeric metrics (currency, percentage, number)
      if ([METRIC_FORMATS.CURRENCY, METRIC_FORMATS.PERCENTAGE, METRIC_FORMATS.NUMBER, METRIC_FORMATS.MULTIPLE].includes(variant.format)) {
        fields.push(variant.field);
      }
    }
  }

  return fields;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if a metric requires PRO subscription
 * @param {string} fieldName - Field name to check
 * @returns {boolean} True if PRO required
 */
export const isProMetric = (fieldName) => {
  const metric = findMetricByField(fieldName);
  return metric?.proMetric || false;
};

/**
 * Get format type for a metric
 * @param {string} fieldName - Field name
 * @returns {string} Format type from METRIC_FORMATS
 */
export const getMetricFormat = (fieldName) => {
  const metric = findMetricByField(fieldName);
  return metric?.format || METRIC_FORMATS.NUMBER;
};

/**
 * Get metric type (flow or instant) for a metric
 * @param {string} fieldName - Field name
 * @returns {string} Metric type from METRIC_TYPES (flow or instant)
 */
export const getMetricType = (fieldName) => {
  const metricVariant = findMetricByField(fieldName);
  if (!metricVariant || !metricVariant.baseMetric) {
    return METRIC_TYPES.INSTANT; // Default to instant if not found
  }
  return metricVariant.baseMetric.metricType || METRIC_TYPES.INSTANT;
};

/**
 * Get all unique groups
 * @returns {Array} Array of group names
 */
export const getAllGroups = () => {
  return Object.values(METRIC_GROUPS);
};

/**
 * Get display metadata for a metric
 * @param {string} fieldName - Field name
 * @returns {Object} Metadata object
 */
export const getMetricMetadata = (fieldName) => {
  const metric = findMetricByField(fieldName);
  return metric?.metadata || {};
};

// ============================================
// COMPSHEET COLUMN BUILDERS
// ============================================

/**
 * Get all metrics suitable for CompSheet display
 * @param {boolean} isPaid - Whether user has paid subscription
 * @param {Object} options - Configuration options
 * @param {Array} options.includeMetrics - Specific field names to include (if null, includes all)
 * @param {Array} options.excludeMetrics - Field names to exclude
 * @returns {Array} Array of metric variants with column-building metadata
 */
export const getCompSheetMetrics = (isPaid, options = {}) => {
  const { includeMetrics = null, excludeMetrics = [] } = options;

  let metrics = getMetricsForView('compSheet', isPaid);

  if (includeMetrics) {
    metrics = metrics.filter(m => includeMetrics.includes(m.field));
  }
  if (excludeMetrics.length > 0) {
    metrics = metrics.filter(m => !excludeMetrics.includes(m.field));
  }

  return metrics;
};

/**
 * Build AG Grid column definition from a metric variant
 * @param {Object} metricVariant - Metric variant from registry (with group from baseMetric)
 * @param {Object} options - Configuration options
 * @param {Object} options.cellRenderers - Map of format type to cell renderer function
 * @param {Function} options.comparator - Numerical comparator function
 * @param {Object} options.customProps - Additional AG Grid properties to merge
 * @param {Object} options.CustomHeader - Custom header component
 * @returns {Object} AG Grid column definition
 */
export const buildCompSheetColumnDef = (metricVariant, options = {}) => {
  const {
    cellRenderers = {},
    comparator,
    customProps = {},
    CustomHeader,
  } = options;

  const format = metricVariant.format;
  const metadata = metricVariant.metadata || {};

  // Build headerComponentParams from variant
  const headerComponentParams = {
    displayGraph: metadata.displayGraph || false,
    displayScatter: metadata.displayScatter || false,
  };

  if (metricVariant.sublabel) {
    headerComponentParams.sublabel = metricVariant.sublabel;
  }

  // Get cell renderer based on format
  const getCellRenderer = () => {
    if (cellRenderers[format]) return cellRenderers[format];
    // Return based on format type for special cases
    switch (format) {
      case METRIC_FORMATS.CURRENCY:
        return cellRenderers.valuationCellRenderer;
      case METRIC_FORMATS.PERCENTAGE:
        return cellRenderers.percentageCellRenderer;
      case METRIC_FORMATS.MULTIPLE:
        return cellRenderers.multipleCellRenderer;
      case METRIC_FORMATS.MONETARY_KPI:
        return cellRenderers.revenuePerCustomerRenderer;
      case METRIC_FORMATS.NUMBER:
        return cellRenderers.numberCellRenderer || cellRenderers.paybackCellRenderer;
      default:
        return undefined;
    }
  };

  // Get default width based on format
  const getDefaultWidth = () => {
    switch (format) {
      case METRIC_FORMATS.CURRENCY:
        return { maxWidth: 200, width: 85, minWidth: 85 };
      case METRIC_FORMATS.PERCENTAGE:
        return { maxWidth: 150, width: 80, minWidth: 80 };
      case METRIC_FORMATS.MULTIPLE:
        return { maxWidth: 200, width: 85, minWidth: 85 };
      case METRIC_FORMATS.MONETARY_KPI:
        return { maxWidth: 200, width: 84, minWidth: 84 };
      case METRIC_FORMATS.NUMBER:
        return { maxWidth: 150, width: 80, minWidth: 80 };
      default:
        return { maxWidth: 150, width: 80, minWidth: 80 };
    }
  };

  // Build base column definition
  const colDef = {
    field: metricVariant.field,
    headerName: metricVariant.headerName || metricVariant.field,
    group: metricVariant.group,
    proMetric: metricVariant.proMetric || false,
    filter: 'agNumberColumnFilter',
    floatingFilter: false,
    ...getDefaultWidth(),
  };

  // Add cell renderer if available
  const cellRenderer = getCellRenderer();
  if (cellRenderer) {
    colDef.cellRenderer = cellRenderer;
  }

  // Add comparator for percentage fields (to keep Mean/Median at bottom)
  if (comparator && format === METRIC_FORMATS.PERCENTAGE) {
    colDef.comparator = comparator;
  }

  // Add custom header component
  if (CustomHeader) {
    colDef.headerComponent = CustomHeader;
    colDef.headerComponentFramework = CustomHeader;
    colDef.headerComponentParams = headerComponentParams;
  }

  // Merge variant-level overrides, then custom props (overrides defaults)
  const colDefOverrides = metricVariant.colDefOverrides || {};
  return { ...colDef, ...colDefOverrides, ...customProps };
};

/**
 * Build complete array of CompSheet column definitions
 * @param {boolean} isPaid - Whether user has paid subscription
 * @param {Object} options - Configuration options
 * @param {Function} options.companyCellRenderer - Cell renderer for company name column
 * @param {Object} options.cellRenderers - Map of format type to cell renderer
 * @param {Function} options.comparator - Numerical comparator for sorting
 * @param {Function} options.dateFormatter - Value formatter for dates
 * @param {Function} options.dateComparator - Comparator for date filter UI
 * @param {Function} options.dateSortComparator - Comparator for date column sorting
 * @param {Object} options.CustomHeader - Custom header component
 * @param {Array} options.entityColumns - Entity column definitions to prepend
 * @param {Array} options.includeMetrics - Specific metrics to include (null = all)
 * @param {Array} options.excludeMetrics - Metrics to exclude
 * @returns {Array} Complete array of AG Grid column definitions
 */
export const buildCompSheetColumnDefs = (isPaid, options = {}) => {
  const {
    companyCellRenderer,
    cellRenderers = {},
    comparator,
    dateFormatter,
    dateComparator,
    dateSortComparator,
    CustomHeader,
    entityColumns = [],
    includeMetrics = null,
    excludeMetrics = [],
  } = options;

  const colDefs = [];

  // Add entity columns first (these are passed in, not from registry)
  entityColumns.forEach(entityCol => {
    const col = { ...entityCol };

    // Apply special handlers for entity columns
    if (entityCol.isCompanyColumn && companyCellRenderer) {
      col.cellRenderer = companyCellRenderer;
    }
    if (entityCol.isDateColumn) {
      if (dateFormatter) col.valueFormatter = dateFormatter;
      if (dateComparator) col.filterParams = { comparator: dateComparator };
      if (dateSortComparator) col.comparator = dateSortComparator;
    }
    if (CustomHeader) {
      col.headerComponent = CustomHeader;
      if (entityCol.isCompanyColumn) {
        col.headerComponentParams = { displayLogo: true };
      }
    }

    colDefs.push(col);
  });

  // Get metrics and build column defs
  const metrics = getCompSheetMetrics(isPaid, { includeMetrics, excludeMetrics });

  // Sort metrics by group order, then by field name within group
  const groupOrder = [
    METRIC_GROUPS.TOP_LINE,
    METRIC_GROUPS.UNIT_ECONOMICS,
    METRIC_GROUPS.GROWTH,
    METRIC_GROUPS.BUSINESS_COMPOSITION,
    METRIC_GROUPS.EFFICIENCY,
    METRIC_GROUPS.PROFITABILITY,
  ];

  const companyStatsFieldOrder = new Map([
    'Market Cap',
    'EV/Sales NTM',
    'EV/GM NTM',
    'EV/Sales TTM',
    'EV/GM TTM',
    'Price/FCF TTM',
  ].map((field, index) => [field, index]));

  const compareFieldsWithinGroup = (a, b) => {
    if (a.group === METRIC_GROUPS.COMPANY_STATS) {
      const rankA = companyStatsFieldOrder.get(a.field);
      const rankB = companyStatsFieldOrder.get(b.field);
      if (rankA !== undefined || rankB !== undefined) {
        if (rankA === undefined) return 1;
        if (rankB === undefined) return -1;
        return rankA - rankB;
      }
    }

    if (a.group === METRIC_GROUPS.TOP_LINE) {
      const getToplineRank = (field) => {
        if (field === 'ARR YOY%') return 1;
        if (field.startsWith('TTM ')) return 2;
        return 0;
      };
      const rankA = getToplineRank(a.field);
      const rankB = getToplineRank(b.field);
      if (rankA !== rankB) return rankA - rankB;
    }

    return a.field.localeCompare(b.field);
  };

  metrics.sort((a, b) => {
    const groupIndexA = groupOrder.indexOf(a.group);
    const groupIndexB = groupOrder.indexOf(b.group);
    if (groupIndexA !== groupIndexB) return groupIndexA - groupIndexB;
    return compareFieldsWithinGroup(a, b);
  });

  // Build column definition for each metric
  metrics.forEach(metric => {
    const colDef = buildCompSheetColumnDef(metric, {
      cellRenderers,
      comparator,
      CustomHeader,
    });
    colDefs.push(colDef);
  });

  return colDefs;
};

/**
 * Get all CompSheet metrics (both paid and free) for column field selection
 * Used for populating column selection dropdowns
 * @returns {Array} Array of all metric variants with field and proMetric info
 */
export const getAllCompSheetMetricFields = () => {
  const fields = [];

  for (const metric of Object.values(METRICS_REGISTRY)) {
    for (const [type, variant] of Object.entries(metric.variants)) {
      fields.push({
        field: variant.field,
        headerName: variant.headerName || variant.field,
        group: variant.group ?? metric.group,
        proMetric: variant.proMetric || false,
        sublabel: variant.sublabel,
      });
    }
  }

  return fields;
};
